import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router';

import getSettingItems from '@/structure/settings';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarMenuSub,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';

import { NavUser } from '@/components/ui/nav-user';

import {
  privateNavbarMenu,
  bmPlusNavbarMenu,
  servicesNavbarMenu,
} from '@/structure/menu';

import useDashboardLinkPrefix from '@/hooks/useDashboardLinkPrefix';
import { getPropertyAddress } from '@/utils/properties';
import BoligmappaHeaderLogo from '@/assets/images/BM-header-logo.svg';
import Home from '@/assets/icons/Home';
import HomeBoldIcon from '@/assets/icons/HomeBoldIcon';
import { getDashboardLinkPrefix } from '@/utils/routes';

export function AppSidebar({
  ownedProperties,
  sharedProperties,
  activeProperty,
  activeUser,
}) {
  const [selectedPropertyId, setSelectedPropertyId] = useState(0);
  const history = useHistory();
  const { pathname } = useLocation();
  const dashboardLinkPrefix = useDashboardLinkPrefix();
  const propertyMenuItems = privateNavbarMenu.filter(
    (menuItem) => !menuItem.mobileOnly
  );

  const profileMenuSections = getSettingItems({
    dashboardLinkPrefix,
    propertyDashboardLinkPrefix: getDashboardLinkPrefix(
      activeProperty?.boligmappaNumber
    ),
  })
    .filter(({ mobileOnly }) => !mobileOnly)
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (el) => !el.propertyRequired || activeProperty?.owned
      ),
    }));

  const onPropertyChange = (propertyItem) => {
    history.push(`/dashboard/${propertyItem.boligmappaNumber}/home`);
  };
  
  useEffect(() => {
    if (activeProperty?.boligmappaNumber && !!ownedProperties?.length) {
      setSelectedPropertyId(
        [...ownedProperties, ...sharedProperties]?.find(
          (propertyItem) =>
            propertyItem.boligmappaNumber === activeProperty.boligmappaNumber
        )?.id
      );
    } else if (!!ownedProperties?.length) {
      setSelectedPropertyId(ownedProperties[0].id);
    } else if (!!sharedProperties?.length) {
      setSelectedPropertyId(sharedProperties[0].id);
    } else {
      setSelectedPropertyId(0);
    }
  }, [activeProperty, ownedProperties]);

  const setHomeIconLink = () => {
    if (!!ownedProperties?.length) {
      return `/dashboard/${ownedProperties[0]?.boligmappaNumber}/home`;
    } else if (!!sharedProperties?.length) {
      return `/dashboard/${sharedProperties[0]?.boligmappaNumber}/home`;
    }
    return 'personal-area/dashboard/boligmappa-plus';
  };

  return (
    <Sidebar className="font-circular">
      <Link to={setHomeIconLink()} className="flex content-start">
        <img
          className="max-h-[18px] m-4"
          src={BoligmappaHeaderLogo}
          alt="Logo"
        />
      </Link>
      <SidebarContent>
        {!!ownedProperties?.length && (
          <SidebarGroup>
            <SidebarGroupLabel>Dine boliger</SidebarGroupLabel>
            {ownedProperties?.map((propertyItem, index) => (
              <Collapsible
                key={propertyItem.id}
                open={propertyItem.id === selectedPropertyId}
                className="group/collapsible"
              >
                <CollapsibleTrigger
                  asChild
                  onClick={() => onPropertyChange(propertyItem)}
                >
                  <SidebarMenuButton>
                    <SidebarGroupLabel className="p-0 gap-2 text-sidebarMenu">
                      {index === 0 ? (
                        <HomeBoldIcon />
                      ) : (
                        <Home />
                      )}
                      {getPropertyAddress(propertyItem)}
                    </SidebarGroupLabel>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenuSub>
                      <SidebarMenu>
                        {propertyMenuItems.map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton
                              className="data-[active=true]:text-primary"
                              asChild
                              isActive={
                                !pathname.includes('personal-area') &&
                                pathname.includes(item.link)
                              }
                            >
                              <Link
                                className="data-[active=true]:shadow data-[active=true]:rounded-lg"
                                to={`${getDashboardLinkPrefix(
                                  activeProperty?.boligmappaNumber ||
                                    ownedProperties[0].boligmappaNumber
                                )}/${item.link}`}
                              >
                                {!pathname.includes('personal-area') &&
                                pathname.includes(item.link) ? (
                                  <item.activeIcon />
                                ) : (
                                  <item.icon />
                                )}
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarMenuSub>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </SidebarGroup>
        )}

        {!!sharedProperties?.length && (
          <SidebarGroup>
            <SidebarGroupLabel>Delt med deg</SidebarGroupLabel>
            {sharedProperties.map((sharedPropertyItem) => (
              <Collapsible
                key={sharedPropertyItem.id}
                open={sharedPropertyItem.id === selectedPropertyId}
                className="group/collapsible"
              >
                <CollapsibleTrigger
                  asChild
                  onClick={() => onPropertyChange(sharedPropertyItem)}
                >
                  <SidebarMenuButton>
                    <SidebarGroupLabel className="p-0 gap-2 text-sidebarMenu">
                      <Home />
                      {getPropertyAddress(sharedPropertyItem)}
                    </SidebarGroupLabel>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenuSub>
                      <SidebarMenu>
                        {propertyMenuItems.map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton
                              className="data-[active=true]:text-primary"
                              asChild
                              isActive={
                                !pathname.includes('personal-area') &&
                                pathname.includes(item.link)
                              }
                            >
                              <Link
                                className="data-[active=true]:shadow data-[active=true]:rounded-lg"
                                to={`${getDashboardLinkPrefix(
                                  activeProperty?.boligmappaNumber
                                )}/${item.link}`}
                              >
                                {!pathname.includes('personal-area') &&
                                pathname.includes(item.link) ? (
                                  <item.activeIcon />
                                ) : (
                                  <item.icon />
                                )}
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarMenuSub>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </SidebarGroup>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>Boligmappa+</SidebarGroupLabel>
          <SidebarMenu>
            {bmPlusNavbarMenu.map(
              (item) =>
                (item.propertyRequired &&
                  !activeProperty.id &&
                  !ownedProperties?.length) || (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname.includes(item.link)}
                    >
                      <Link
                        to={
                          !item.propertyRequired
                            ? item.link
                            : `${getDashboardLinkPrefix(
                                activeProperty?.boligmappaNumber ||
                                  ownedProperties[0]?.boligmappaNumber
                              )}/${item.link}`
                        }
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
            )}
          </SidebarMenu>
        </SidebarGroup>
        {activeProperty?.boligmappaNumber && (
          <SidebarGroup>
            <SidebarGroupLabel>Tjenester</SidebarGroupLabel>
            <SidebarMenu>
              {servicesNavbarMenu.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname.includes(item.link)}
                  >
                    <Link
                      to={`${getDashboardLinkPrefix(
                        activeProperty?.boligmappaNumber
                      )}/${item.link}`}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          user={{
            name: activeUser.fullName,
            email: activeUser.email,
            avatarNameLetters:
              activeUser.firstName !== undefined &&
              activeUser.firstName[0] + activeUser.lastName[0],
          }}
          profileMenuSections={profileMenuSections}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
